import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { WHATSAPP_LINK } from '../utils/constants';

const faqs = [
  {
    q: 'How can I track my shipment in real time?',
    a: 'Once your booking is confirmed, you will receive a unique Tracking ID on your registered WhatsApp number. Enter this ID on our Track page at pkroutex.com/track to see the live GPS location of your truck, driver details, and estimated delivery time.',
  },
  {
    q: 'What types of trucks and vehicles do you provide?',
    a: 'We offer a comprehensive fleet including Dost Plus (10ft), Bada Dost i5 (12ft), Eicher 2059XP (14ft), and heavy commercial trucks from 17ft to 32ft. Each vehicle is GPS-enabled, regularly maintained, and operated by verified professional drivers.',
  },
  {
    q: 'Do you provide insurance for goods in transit?',
    a: 'Yes. All shipments handled by PKROUTEX are covered under comprehensive transit insurance. In the unlikely event of damage or loss, our claims team will assist you through the entire process with minimal paperwork.',
  },
  {
    q: 'Which regions and cities do you cover?',
    a: 'We operate an extensive pan-India network covering 250+ cities including all major metros, tier-2 industrial hubs, and key logistics corridors. Our primary strength lies in South India routes including Tamil Nadu, Karnataka, Andhra Pradesh, Kerala, and Telangana.',
  },
  {
    q: 'How quickly can you dispatch a vehicle after booking?',
    a: 'For standard bookings, we dispatch within 2-4 hours. For express bookings, same-day dispatch is available in most serviceable cities. Our operations team works 24/7 to ensure your cargo moves without delay.',
  },
  {
    q: 'Can I book a part load (LTL) if I have a small shipment?',
    a: 'Absolutely. Our Part Load (LTL) service lets you pay only for the space your cargo occupies. This is ideal for small businesses, e-commerce sellers, and anyone shipping less than a full truckload. Same tracking and insurance benefits apply.',
  },
  {
    q: 'How do I get a price quote for my shipment?',
    a: 'Fill out the booking form on our website or send us a WhatsApp message with your pickup location, destination, vehicle type, and cargo details. Our team will respond with a competitive quote within 15 minutes during business hours.',
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="section-padding bg-brand-bg relative overflow-hidden">
      {/* Background */}
      <motion.div
        animate={{ scale: [1, 1.1, 1], opacity: [0.03, 0.06, 0.03] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-orange rounded-full blur-[150px] pointer-events-none"
      />

      <div className="max-w-3xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-brand-orange/5 border border-brand-orange/20 rounded-full px-5 py-2 mb-6">
            <HelpCircle size={12} className="text-brand-orange" />
            <span className="text-brand-orange font-black tracking-[0.4em] text-xs uppercase">Got Questions?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tighter text-brand-black mb-4">
            FREQUENTLY <span className="text-brand-orange">ASKED</span>
          </h2>
          <p className="text-brand-muted">Everything you need to know about PKROUTEX logistics services.</p>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.06 }}
              className={`border rounded-2xl overflow-hidden bg-white shadow-sm transition-all duration-300 ${
                openIndex === idx ? 'border-brand-orange/30 shadow-md shadow-brand-orange/5' : 'border-black/8 hover:border-black/15'
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left group"
              >
                <div className="flex items-center gap-4">
                  <span className={`text-xs font-black tabular-nums shrink-0 transition-colors duration-300 ${openIndex === idx ? 'text-brand-orange' : 'text-black/20'}`}>
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className={`font-bold text-sm sm:text-base md:text-lg transition-colors duration-300 ${openIndex === idx ? 'text-brand-orange' : 'text-brand-black'}`}>
                    {faq.q}
                  </span>
                </div>
                <motion.div
                  animate={{ rotate: openIndex === idx ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className={`shrink-0 ml-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                    openIndex === idx ? 'bg-brand-orange text-white' : 'bg-brand-bg text-brand-orange'
                  }`}
                >
                  <ChevronDown size={16} />
                </motion.div>
              </button>

              <AnimatePresence>
                {openIndex === idx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 md:px-6 pb-5 md:pb-6 pl-14 md:pl-16 text-brand-muted text-sm leading-relaxed border-t border-black/5 pt-4">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center p-8 bg-white rounded-3xl border border-black/8 shadow-sm"
        >
          <p className="text-brand-black font-black text-lg mb-2">Still have questions?</p>
          <p className="text-brand-muted text-sm mb-6">Our team is available 24/7 to help you with any logistics query.</p>
          <motion.a
            href={WHATSAPP_LINK('Hi PKRouteX! I have a question.')}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-3.5 rounded-full font-black text-sm tracking-widest uppercase orange-glow hover:bg-brand-orange-light transition-all"
          >
            Chat on WhatsApp
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
